import { Users, Zap, Shield, Award } from 'lucide-react';

// Tesla Model Y - Black SUV
const MODEL_Y_IMAGE = 'https://images.unsplash.com/photo-1615051050993-9502ba54aeff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxUZXNsYSUyME1vZGVsJTIwWSUyMGJsYWNrJTIwU1VWfGVufDB8fHx8MTc3NTc1OTkzNnww&ixlib=rb-4.1.0&q=85';

// Tesla Model 3 - Black Sedan
const MODEL_3_IMAGE = 'https://images.unsplash.com/photo-1728763913553-e549becf9b63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxUZXNsYSUyME1vZGVsJTIwMyUyMGJsYWNrJTIwc2VkYW4lMjBlbGVjdHJpY3xlbnwwfHx8fDE3NzU3NTk5NDJ8MA&ixlib=rb-4.1.0&q=85';

export const FleetSection = ({ t, lang }) => {
  const vehicles = [
    {
      id: 'model_y',
      name: t('vehicles.modelY.name'),
      description: t('vehicles.modelY.description'),
      image: MODEL_Y_IMAGE,
      features: ['4 passengers', '100% Electric', 'WiFi & Chargers']
    },
    {
      id: 'model_3',
      name: t('vehicles.model3.name'),
      description: t('vehicles.model3.description'),
      image: MODEL_3_IMAGE,
      features: ['3 passengers', '100% Electric', 'Premium Interior']
    }
  ];

  return (
    <section id="fleet" className="py-24 lg:py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <span className="label-luxury">{t('nav.fleet')}</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4 tracking-tighter" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Tesla Model Y & Model 3
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {vehicles.map((vehicle) => (
            <div 
              key={vehicle.id}
              className="group glass-card overflow-hidden"
              data-testid={`fleet-card-${vehicle.id}`}
            >
              {/* Image */}
              <div className="h-64 lg:h-72 overflow-hidden">
                <img 
                  src={vehicle.image} 
                  alt={vehicle.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              
              {/* Content */}
              <div className="p-6 lg:p-8">
                <h3 className="text-2xl lg:text-3xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {vehicle.name}
                </h3>
                <p className="text-white/50 text-sm mb-6">
                  {vehicle.description}
                </p>
                
                {/* Features */}
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <Users size={16} className="text-[#C5A059]" />
                    <span>{vehicle.features[0]}</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <Zap size={16} className="text-[#C5A059]" />
                    <span>{vehicle.features[1]}</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/70 text-sm">
                    <Shield size={16} className="text-[#C5A059]" />
                    <span>{vehicle.features[2]}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing info */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col glass-card p-6 lg:p-8">
            {/* Tiered pricing table */}
            <h4 className="label-luxury mb-4">
              {lang === 'fr' ? 'Tarification par tranches' : 'Tiered Pricing'}
            </h4>
            <div className="grid grid-cols-3 gap-4 mb-6 text-sm">
              <div className="text-center">
                <div className="text-white/50 text-xs mb-1">0 → 50 km</div>
                <div className="text-[#C5A059] text-lg">3,50€<span className="text-white/50 text-xs">/km</span></div>
              </div>
              <div className="text-center border-x border-white/10 px-4">
                <div className="text-white/50 text-xs mb-1">50 → 200 km</div>
                <div className="text-[#C5A059] text-lg">2,65€<span className="text-white/50 text-xs">/km</span></div>
              </div>
              <div className="text-center">
                <div className="text-white/50 text-xs mb-1">200+ km</div>
                <div className="text-[#C5A059] text-lg">2,20€<span className="text-white/50 text-xs">/km</span></div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 pt-4 border-t border-white/10">
              <div>
                <span className="label-luxury">{lang === 'fr' ? 'Mise à disposition' : 'Hourly Rental'}</span>
                <div className="text-2xl text-white mt-1">60€<span className="text-white/50 text-base">/h</span></div>
              </div>
              <div className="hidden sm:block w-px h-12 bg-white/10"></div>
              <div>
                <span className="label-luxury">{lang === 'fr' ? 'Majoration nuit' : 'Night Surcharge'}</span>
                <div className="text-2xl text-white mt-1">+20%</div>
                <div className="text-white/40 text-xs">22h-6h</div>
              </div>
            </div>
          </div>
        </div>

        {/* Loyalty program banner */}
        <div className="mt-8 text-center" data-testid="loyalty-banner">
          <div className="inline-flex items-center gap-3 bg-[#C5A059]/10 border border-[#C5A059]/30 px-6 py-4">
            <Award className="text-[#C5A059]" size={24} />
            <div className="text-left">
              <p className="text-[#C5A059] font-medium">
                {lang === 'fr' ? 'Programme fidélité' : 'Loyalty Program'}
              </p>
              <p className="text-white/70 text-sm">
                {lang === 'fr' 
                  ? '–10% à partir de votre 5ᵉ trajet avec Interflex Transport'
                  : '–10% from your 5th trip with Interflex Transport'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FleetSection;
